/**
 * NewLeadForm.tsx — operator-initiated manual lead creation (story E0-BO-LS12).
 *
 * Six fields per the design spec's Seam 1 contract
 * (docs/superpowers/specs/2026-07-28-backoffice-manual-lead-creation-design.md):
 * phone, display name, tenant, product, lawful basis, and a conditional source
 * note. `POST /api/leads` never carries an operator/actor field — the BFF reads
 * identity from the authenticated principal (ADR-056) and would reject one
 * supplied here; this form never attempts to send one.
 *
 * Tenant picker (D2): the BFF's `GET /api/leads/form-options` returns exactly
 * one tenant for v1. The field is rendered as a real (disabled) picker — not
 * hidden — so the surface is ready for a genuine multi-tenant list later
 * without a redesign, but no selection logic is built for it now.
 *
 * Source note (D3): capped at 500 characters client-side as a convenience; the
 * server cap is authoritative (spec, Error handling). Required only when
 * `lawful_basis === 'legitimate_interest'`.
 *
 * Error handling mirrors LeadTransitionForm's distinct-status posture — never
 * one generic message:
 *   422 → field-level "fix the highlighted fields" banner; nothing written.
 *   409 → "this number already has a lead" + a link to the existing lead
 *         (`ManualLeadConflict.existing_lead_id`); nothing written.
 *   401/403 → "not authorized".
 *   otherwise (502 / transport / 5xx) → "unavailable"; the form keeps every
 *         value so nothing the operator typed is lost.
 * The form is cleared only on success, right before navigating away.
 */

import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import {
  SOURCE_NOTE_MAX_LENGTH,
  type LawfulBasis,
  type LeadFormOptions,
  type ManualLeadConflict,
} from '../../api/types';
import { Field, inputClass } from '../../components/Field';
import { Button } from '../../components/Button';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface NewLeadFormProps {
  apiClient: ApiClient;
  /** Called with the new lead's id on a successful 201. */
  onCreated: (leadId: string) => void;
  onCancel: () => void;
}

type OptionsPhase = 'loading' | 'ready' | 'error' | 'unauthorized';

interface FieldErrors {
  phone?: string;
  displayName?: string;
  product?: string;
  lawfulBasis?: string;
  sourceNote?: string;
}

type WriteError =
  | { kind: 'invalid' }
  | { kind: 'conflict'; existingLeadId: string }
  | { kind: 'unavailable' }
  | { kind: 'unauthorized' };

// A loose E.164 shape check (leading '+', 7–15 digits). The server validates
// authoritatively (Seam 1); this is a convenience, not the control.
const E164_PATTERN = /^\+[1-9]\d{6,14}$/;
const WriteErrorBanner = ({ error }: { error: WriteError }) => {
  const { t } = useTranslation();
  const baseClass =
    'rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40';

  if (error.kind === 'invalid') {
    return (
      <p role="alert" data-testid="new-lead-error-invalid" className={baseClass}>
        {t('leads.newLead.errors.invalid')}
      </p>
    );
  }
  if (error.kind === 'conflict') {
    return (
      <div role="alert" data-testid="new-lead-error-conflict" className="flex flex-col gap-2">
        <p className={baseClass}>{t('leads.newLead.errors.conflict')}</p>
        <a
          href={`#leads/${error.existingLeadId}`}
          data-testid="new-lead-conflict-link"
          className="w-fit rounded text-gold underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-outline-focus"
        >
          {t('leads.newLead.errors.conflictLink')}
        </a>
      </div>
    );
  }
  if (error.kind === 'unauthorized') {
    return (
      <p role="alert" data-testid="new-lead-error-unauthorized" className={baseClass}>
        {t('leads.newLead.errors.unauthorized')}
      </p>
    );
  }
  return (
    <p role="alert" data-testid="new-lead-error-unavailable" className={baseClass}>
      {t('leads.newLead.errors.unavailable')}
    </p>
  );
};

const NewLeadFormBody = ({ apiClient, onCreated, onCancel }: NewLeadFormProps) => {
  const { t } = useTranslation();
  const formId = useId();

  const [optionsPhase, setOptionsPhase] = useState<OptionsPhase>('loading');
  const [options, setOptions] = useState<LeadFormOptions | null>(null);

  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [lawfulBasis, setLawfulBasis] = useState<LawfulBasis | ''>('');
  const [sourceNote, setSourceNote] = useState('');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [writeError, setWriteError] = useState<WriteError | null>(null);

  const loadOptions = () => {
    setOptionsPhase('loading');
    apiClient
      .getLeadFormOptions()
      .then((result) => {
        setOptions(result);
        setOptionsPhase('ready');
      })
      .catch((error: unknown) => {
        setOptionsPhase(error instanceof UnauthorizedError ? 'unauthorized' : 'error');
      });
  };

  useEffect(loadOptions, [apiClient]);

  const noteRequired = lawfulBasis === 'legitimate_interest';
  const tenant = options?.tenants[0] ?? null;

  // Per-field validators, shared by validate() (on submit) and revalidate()
  // (on change) so a field's error clears the moment its value becomes valid
  // instead of sitting stale until the next submit attempt.
  const validatePhone = (value: string) =>
    E164_PATTERN.test(value.trim()) ? undefined : t('leads.newLead.errors.phoneRequired');
  const validateDisplayName = (value: string) =>
    value.trim() === '' ? t('leads.newLead.errors.displayNameRequired') : undefined;
  const validateProduct = (value: string) =>
    value === '' ? t('leads.newLead.errors.productRequired') : undefined;
  const validateLawfulBasis = (value: LawfulBasis | '') =>
    value === '' ? t('leads.newLead.errors.lawfulBasisRequired') : undefined;
  const validateSourceNote = (value: string, required: boolean) => {
    const trimmed = value.trim();
    if (required && trimmed === '') return t('leads.newLead.errors.sourceNoteRequired');
    if (trimmed.length > SOURCE_NOTE_MAX_LENGTH) {
      return t('leads.newLead.errors.sourceNoteTooLong', { max: SOURCE_NOTE_MAX_LENGTH });
    }
    return undefined;
  };

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    const phoneError = validatePhone(phone);
    if (phoneError != null) errors.phone = phoneError;
    const displayNameError = validateDisplayName(displayName);
    if (displayNameError != null) errors.displayName = displayNameError;
    const productError = validateProduct(productCode);
    if (productError != null) errors.product = productError;
    const lawfulBasisError = validateLawfulBasis(lawfulBasis);
    if (lawfulBasisError != null) errors.lawfulBasis = lawfulBasisError;
    const sourceNoteError = validateSourceNote(sourceNote, noteRequired);
    if (sourceNoteError != null) errors.sourceNote = sourceNoteError;
    return errors;
  };

  // Only revalidates a field that already has an error rendered — mirrors
  // React Hook Form's default reValidateMode: 'onChange' (clear as soon as
  // the value becomes valid; don't surface new errors before first submit).
  const revalidate = (field: keyof FieldErrors, error: string | undefined) => {
    setFieldErrors((prev) => (prev[field] == null ? prev : { ...prev, [field]: error }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (tenant == null || submitting) return;

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setWriteError(null);
    try {
      const trimmedNote = sourceNote.trim();
      const response = await apiClient.createLead({
        phone: phone.trim(),
        display_name: displayName.trim(),
        tenant_id: tenant.id,
        product_code: productCode,
        lawful_basis: lawfulBasis as LawfulBasis,
        ...(trimmedNote !== '' ? { source_note: trimmedNote } : {}),
      });
      onCreated(response.lead_id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        setWriteError({ kind: 'invalid' });
      } else if (error instanceof ApiError && error.status === 409) {
        const conflict = error.body as ManualLeadConflict | null;
        setWriteError({
          kind: 'conflict',
          existingLeadId: conflict?.existing_lead_id ?? '',
        });
      } else if (error instanceof UnauthorizedError) {
        setWriteError({ kind: 'unauthorized' });
      } else {
        setWriteError({ kind: 'unavailable' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (optionsPhase === 'unauthorized') {
    return (
      <p role="alert" data-testid="new-lead-unauthorized" className="text-body text-status-down">
        {t('leads.newLead.errors.unauthorized')}
      </p>
    );
  }

  if (optionsPhase === 'loading') {
    return (
      <Skeleton data-testid="new-lead-options-loading" label={t('leads.newLead.loadingOptions')} />
    );
  }

  if (optionsPhase === 'error' || options == null) {
    return (
      <Notice
        tone="danger"
        data-testid="new-lead-options-error"
        action={
          <Button data-testid="new-lead-options-retry" onClick={loadOptions}>
            {t('leads.newLead.retry')}
          </Button>
        }
      >
        {t('leads.newLead.optionsError')}
      </Notice>
    );
  }

  return (
    <form
      aria-labelledby={`${formId}-heading`}
      data-testid="new-lead-form"
      className="flex flex-col gap-3"
      onSubmit={(event) => floating(handleSubmit(event))}
      noValidate
    >
      <h2 id={`${formId}-heading`} className="text-h3 text-ink-1">
        {t('leads.newLead.heading')}
      </h2>

      <Field
        id={`${formId}-phone`}
        label={t('leads.newLead.phone')}
        hint={t('leads.newLead.phoneHint')}
        error={fieldErrors.phone}
      >
        <input
          id={`${formId}-phone`}
          data-testid="new-lead-phone"
          type="tel"
          value={phone}
          disabled={submitting}
          aria-invalid={fieldErrors.phone != null}
          onChange={(event) => {
            const { value } = event.target;
            setPhone(value);
            revalidate('phone', validatePhone(value));
          }}
          className={inputClass}
        />
      </Field>

      <Field
        id={`${formId}-name`}
        label={t('leads.newLead.displayName')}
        error={fieldErrors.displayName}
      >
        <input
          id={`${formId}-name`}
          data-testid="new-lead-display-name"
          type="text"
          maxLength={255}
          value={displayName}
          disabled={submitting}
          aria-invalid={fieldErrors.displayName != null}
          onChange={(event) => {
            const { value } = event.target;
            setDisplayName(value);
            revalidate('displayName', validateDisplayName(value));
          }}
          className={inputClass}
        />
      </Field>

      <Field id={`${formId}-tenant`} label={t('leads.newLead.tenant')}>
        {/* D2 — exactly one entry for v1. A real, disabled picker: ready for a
            genuine multi-tenant list later, no selection logic built now. */}
        <select
          id={`${formId}-tenant`}
          data-testid="new-lead-tenant"
          value={tenant?.id ?? ''}
          disabled
          className={inputClass}
        >
          {tenant != null ? <option value={tenant.id}>{tenant.name}</option> : null}
        </select>
      </Field>

      <Field
        id={`${formId}-product`}
        label={t('leads.newLead.product')}
        error={fieldErrors.product}
      >
        <select
          id={`${formId}-product`}
          data-testid="new-lead-product"
          value={productCode}
          disabled={submitting}
          aria-invalid={fieldErrors.product != null}
          onChange={(event) => {
            const { value } = event.target;
            setProductCode(value);
            revalidate('product', validateProduct(value));
          }}
          className={inputClass}
        >
          <option value="">{t('leads.newLead.productChoose')}</option>
          {options.products.map((product) => (
            <option key={product.code} value={product.code}>
              {product.name}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id={`${formId}-basis`}
        label={t('leads.newLead.lawfulBasis')}
        error={fieldErrors.lawfulBasis}
      >
        <select
          id={`${formId}-basis`}
          data-testid="new-lead-lawful-basis"
          value={lawfulBasis}
          disabled={submitting}
          aria-invalid={fieldErrors.lawfulBasis != null}
          onChange={(event) => {
            const value = event.target.value as LawfulBasis;
            setLawfulBasis(value);
            revalidate('lawfulBasis', validateLawfulBasis(value));
          }}
          className={inputClass}
        >
          <option value="">{t('leads.newLead.lawfulBasisChoose')}</option>
          {options.lawful_bases.map((basis) => (
            <option key={basis.value} value={basis.value}>
              {basis.label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id={`${formId}-note`}
        label={noteRequired ? t('leads.newLead.sourceNoteRequired') : t('leads.newLead.sourceNote')}
        hint={noteRequired ? t('leads.newLead.sourceNoteHint') : undefined}
        error={fieldErrors.sourceNote}
      >
        <textarea
          id={`${formId}-note`}
          data-testid="new-lead-source-note"
          rows={3}
          maxLength={SOURCE_NOTE_MAX_LENGTH}
          value={sourceNote}
          disabled={submitting}
          aria-invalid={fieldErrors.sourceNote != null}
          aria-required={noteRequired}
          onChange={(event) => {
            const { value } = event.target;
            setSourceNote(value);
            revalidate('sourceNote', validateSourceNote(value, noteRequired));
          }}
          className={inputClass}
        />
      </Field>
      <p data-testid="new-lead-note-count" className="-mt-2 text-meta text-ink-3">
        {t('leads.newLead.sourceNoteCount', {
          count: sourceNote.length,
          max: SOURCE_NOTE_MAX_LENGTH,
        })}
      </p>

      {writeError != null ? <WriteErrorBanner error={writeError} /> : null}

      <div className="flex gap-3">
        <Button
          type="submit"
          variant="primary"
          data-testid="new-lead-submit"
          disabled={submitting}
          aria-busy={submitting}
          className="min-h-11"
        >
          {submitting ? t('leads.newLead.submitting') : t('leads.newLead.submit')}
        </Button>
        <Button
          data-testid="new-lead-cancel"
          disabled={submitting}
          onClick={onCancel}
          className="min-h-11"
        >
          {t('leads.newLead.cancel')}
        </Button>
      </div>
    </form>
  );
};

/**
 * Side sheet pinned to the right edge: opening the form overlays the page
 * instead of pushing the leads table down (slice 7). Not modal: no focus trap,
 * Escape closes it.
 */
export const NewLeadForm = ({ onCancel, ...rest }: NewLeadFormProps) => {
  // Document-level so Escape closes the sheet whenever it is open, not only
  // when focus is inside it. Still non-modal: no trap, no backdrop.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      role="presentation"
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col gap-3 overflow-y-auto bg-surface-2 p-4 shadow-xl ring-1 ring-inset ring-ink-3/10 sm:p-6"
    >
      <NewLeadFormBody
        // eslint-disable-next-line react/jsx-props-no-spreading -- plain pass-through to the body
        {...rest}
        onCancel={onCancel}
      />
    </div>
  );
};
